import React from 'react'
import { MapLayerControl } from './MapLayerControl'
import { GeolocationControl } from './GeolocationControl'
import { LocationSearchControl } from './LocationSearchControl'
import { POIToggleControl } from './POIToggleControl'
import { PhotoToggleControl } from './PhotoToggleControl'
import { BikePathToggleControl } from './BikePathToggleControl'
import { Toggle3DControl } from './Toggle3DControl'
import { ZoomControl } from './ZoomControl'
import { HighwayToggleControl } from './HighwayToggleControl'
import mapboxgl from 'mapbox-gl'

interface MapControlGroupProps {
  map: mapboxgl.Map | null
  currentLayer: string
  onLayerChange: (layerId: string) => void
  className?: string
  // Optional embed-specific props
  showPOIs?: boolean
  onPOIToggle?: () => void
  showPhotos?: boolean
  onPhotoToggle?: () => void
  showBikePaths?: boolean
  onBikePathToggle?: () => void
  showHighways?: boolean
  onHighwayToggle?: () => void
  embedMode?: boolean
  // 3D control props
  is3DEnabled?: boolean
  onToggle3D?: (enabled: boolean) => void
}

export function MapControlGroup({ 
  map, 
  currentLayer, 
  onLayerChange, 
  className = '',
  showPOIs,
  onPOIToggle,
  showPhotos,
  onPhotoToggle,
  showBikePaths,
  onBikePathToggle,
  showHighways,
  onHighwayToggle,
  embedMode = false,
  is3DEnabled = true,
  onToggle3D
}: MapControlGroupProps) {
  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {/* For embed mode, create Mapbox-style control groups */}
      {embedMode ? (
        <>
          {/* Zoom controls group */}
          <div 
            className="mapboxgl-ctrl mapboxgl-ctrl-group"
            style={{
              margin: 0,
              overflow: 'visible'
            }}
          >
            <ZoomControl 
              map={map}
              className="embed-control"
            />
          </div>
          
          {/* Other controls group */}
          <div 
            className="mapboxgl-ctrl mapboxgl-ctrl-group"
            style={{
              margin: 0,
              overflow: 'visible'
            }}
          >
            <MapLayerControl 
              currentLayer={currentLayer}
              onLayerChange={onLayerChange}
              className="embed-control"
            />
            {onPOIToggle && (
              <POIToggleControl 
                showPOIs={showPOIs || false}
                onToggle={onPOIToggle}
                className="embed-control"
              />
            )}
            {onPhotoToggle && (
              <PhotoToggleControl 
                showPhotos={showPhotos || false}
                onToggle={onPhotoToggle}
                className="embed-control"
              />
            )}
          </div>
        </>
      ) : (
        /* Normal vertical layout for non-embed mode */
        <div className="bg-gray-800/60 backdrop-blur-sm rounded-lg shadow-lg flex flex-col" style={{ width: '32px' }}>
          {/* Map Layer Control */}
          <MapLayerControl 
            currentLayer={currentLayer}
            onLayerChange={onLayerChange}
            className="w-full"
          />
          
          {/* Divider */}
          <div className="border-t border-gray-700/30 mx-1" />
          
          {/* Non-embed controls */}
          <GeolocationControl 
            map={map}
            className="w-full"
          />
          
          {/* Location Search Control for normal mode */}
          <LocationSearchControl 
            map={map}
            className="w-full"
          />
          
          {/* Bike Path Toggle Control */}
          {onBikePathToggle && (
            <BikePathToggleControl 
              showBikePaths={showBikePaths || false}
              onToggle={onBikePathToggle}
              className="w-full"
            />
          )}

          {/* Highway Overlay Toggle Control */}
          {onHighwayToggle && (
            <HighwayToggleControl
              showHighways={showHighways || false}
              onToggle={onHighwayToggle}
              className="w-full"
            />
          )}

          {/* Waypoint Editing Toggle */}
          
          {/* 3D Toggle Control */}
          {onToggle3D && (
            <>
              {/* Divider */}
              <div className="border-t border-gray-700/30 mx-1" />
              <Toggle3DControl 
                map={map}
                is3DEnabled={is3DEnabled}
                onToggle3D={onToggle3D}
                className="w-full"
              />
            </>
          )}
        </div>
      )}
    </div>
  )
}
